import React, { useState, useEffect } from 'react';
import { injectIntl } from 'react-intl';
import globals from 'js/globals';
import { ROLE_COMPANY } from 'js/constants';
import axios from 'axios';
import Loader from 'components/layout/Loader';
import may from "js/security";

/**
 * List of ancestor Companies as checkboxes
 * to modify grantedHoldingAccessWhitelist property of the Client
 *
 * Names of companies are fetched from api.
 */
function ClientRolesHoldingAccess({ entity, parent, changeEntity, disabled, user, intl: { messages } }) {
  const [ancestors, setAncestors] = useState([]);
  const [isLoadingAncestors, setIsLoadingAncestors] = useState(false);

  const entityAncestors = entity?.getAncestors?.() ?? [];
  const entityUsername = entity?.getUsername?.();

  useEffect(() => {
    if (entityAncestors.length === 0) return;
    if (!entityUsername) return;

    setIsLoadingAncestors(true);

    const url = `/api/clients/${entityUsername}/ancestors.json`;
    /* response schema: [ {id: string, company: string }, ] */
    axios
      .get(url)
      .then(({ data }) => {
        /**
         * Includes the root CreacheckAdmin
         * To limit up to the Client's WhiteLabel need adjustment
         * of the ClientBundle\Controller\ClientController::getClientAncestorsAction
         */

        // exclude self
        setAncestors(data.filter(({ id }) => id !== entityUsername));
        setIsLoadingAncestors(false);
      })
      .catch(console.error);
  }, [entityAncestors]);

  const parentUsername = parent?.username;
  useEffect(() => {
    if (!parentUsername) return;

    setIsLoadingAncestors(true);

    const url = `/api/clients/${parentUsername}/ancestors.json`;
    /* response schema: [ {id: string, company: string }, ] */
    axios
      .get(url)
      .then(({ data }) => {
        let collectedAncestors = []; // includes the root CreacheckAdmin

        // stay inside the WhiteLabel
        const { whiteLabelSettings } = globals;
        const whiteLabelOwnerName = whiteLabelSettings?.owner?.id; // TODO: case of foreign WL needs adjustment

        if (whiteLabelOwnerName) {
          const startIndex = Math.max(0, data.findIndex(({ id }) => id === whiteLabelOwnerName));
          collectedAncestors = data.slice(startIndex);
        } else {
          collectedAncestors = data;
        }

        // add the selected parent if not included
        const { roles, username, profile } = parent;
        const isAlreadyIncluded = username && collectedAncestors.find(({ id }) => id === username);
        if (roles.includes(ROLE_COMPANY) && !isAlreadyIncluded) {
          const { company } = profile;
          collectedAncestors.push({ id: username, company });
        }

        setAncestors(collectedAncestors);
        setIsLoadingAncestors(false);
      })
      .catch(console.error);
  }, [parentUsername]);

  const isChecked = username => {
    const whitelist = entity?.getGrantedHoldingAccessWhitelist() ?? [];
    return whitelist.includes(username);
  };

  const onChangeCheckbox = ({ target }) => {
    const { checked, value: username } = target;
    const whitelist = entity?.getGrantedHoldingAccessWhitelist() ?? [];

    let newWhitelist;
    if (checked) {
      newWhitelist = [...whitelist, username];
    } else {
      newWhitelist = whitelist.filter(name => name !== username);
    }

    const newEntity = { ...entity.updateValue('grantedHoldingAccessWhitelist', newWhitelist) };
    changeEntity(newEntity);
  };

  return (
    <>
      <h2>{messages.grant_data_access}</h2>
      {isLoadingAncestors ? (
        <Loader />
      ) : (
        <>
          {ancestors.map(({ id, company }) => {
            const elementId = 'client-ancestor-' + id;
            if (id !== 'CreacheckAdmin') {
              return (
                <div class="checkbox check-success" key={id}>
                  <input
                    disabled={disabled}
                    id={elementId}
                    name="roles[]"
                    type="checkbox"
                    value={id}
                    checked={isChecked(id)}
                    onChange={onChangeCheckbox}
                  />{' '}
                  <label htmlFor={elementId}>
                    {company} ({id})
                  </label>
                </div>
              );
            } else if (may('ROLE_SUPERADMIN', user.roles)) {
              return (
                <div class="checkbox check-success" key={id}>
                  <input
                    disabled={disabled}
                    id={elementId}
                    name="roles[]"
                    type="checkbox"
                    value={id}
                    checked={isChecked(id)}
                    onChange={onChangeCheckbox}
                  />{' '}
                  <label htmlFor={elementId}>
                    {company} ({id})
                  </label>
                </div>
              );
            }
          })}
        </>
      )}
    </>
  );
}

ClientRolesHoldingAccess.defaultProps = {
  entity: null,
  parent: null,
  changeEntity: () => {},
  disabled: false,
};

export default injectIntl(ClientRolesHoldingAccess);
